import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { deleteGroupAsync } from "../../redux/boardSlice";
import { getGroupVisibility, getGroupVisibilityLabel } from "../../utils/groupVisibility";
import ConfirmDialog from "../../components/common/ConfirmDialog";
import "./GroupList.css";

import EditIcon from "../../assets/icons/edit-icon2.svg";
import DeleteIcon from "../../assets/icons/delete-icon.svg";
import DashboardIcon from "../../assets/icons/grid.svg";
import ReportIcon from "../../assets/icons/reports.svg";
import WidgetIcon from "../../assets/icons/widget.svg";

const ITEMS_PER_PAGE = 10;
const GRID_ITEMS_PER_PAGE = 12;

/* Type used to be the group's Dashboard/Report type. That is no longer captured at
   group level, so the column shows the group's visibility instead. */
// const TypePill = ({ groupType }) => {
//   const label = groupType ? groupType.charAt(0).toUpperCase() + groupType.slice(1) : "—";
//   return <span className="grpl-type-pill">{label}</span>;
// };

const TypePill = ({ group }) => (
  <span className={`grpl-type-pill grpl-type-pill--${getGroupVisibility(group)}`}>
    {getGroupVisibilityLabel(group)}
  </span>
);

const TypeIcon = ({ groupType }) => {
  const type = (groupType || "").toLowerCase();
  return (
    <span className={`grpl-type-icon grpl-icon-${type || "default"}`}>
      {/* swap these with your imported SVG images e.g:
          type === "report"    → <img src={ReportIcon} height={16} />
          type === "dashboard" → <img src={DashboardIcon} height={16} />
          type === "widget"    → <img src={WidgetIcon} height={16} />
      */}
      {type === "report" ? (
        <img src={ReportIcon} height={24} alt="report-icon" />
      ) : type === "dashboard" ? (
        <img src={DashboardIcon} height={24} alt="dashboard-icon" />
      ) : (
        <img src={WidgetIcon} height={24} alt="widget-icon" />
      )}
    </span>
  );
};

const EditBtn = ({ onClick, disabled }) =>
  disabled ? (
    <span className="grpl-edit-disabled-wrap">
      <button className="grpl-action-icon grpl-action-edit" disabled title="Edit">
        <img src={EditIcon} alt="edit" height={24} />
      </button>
    </span>
  ) : (
    <button className="grpl-action-icon grpl-action-edit" onClick={onClick} disabled={disabled} title="Edit">
      <img src={EditIcon} alt="edit" height={24} />
    </button>
  );

/* Asks through the shared ConfirmDialog rather than an antd Popconfirm, so every
   confirmation in the app looks the same. The button only raises the request; the
   dialog itself is rendered once by GroupList. */
const DeleteBtn = ({ onClick, disabled }) =>
  disabled ? (
    <span style={{ display: "inline-flex", opacity: 0.35, cursor: "not-allowed" }}>
      <button className="grpl-action-icon" disabled style={{ pointerEvents: "none" }} title="Deletes">
        {/* swap with: <img src={DeleteIcon} alt="delete" height={24} /> */}
        <img src={DeleteIcon} alt="delete" height={24} />
      </button>
    </span>
  ) : (
    <button className="grpl-action-icon grpl-action-delete" onClick={onClick} title="Delete">
      {/* swap with: <img src={DeleteIcon} alt="delete" height={24} /> */}
      <img src={DeleteIcon} alt="delete" height={24} />
    </button>
  );

export default function GroupList({
  groups,
  viewMode,
  loading = false,
  setNewGropModalVisibility,
  handleGroupSelect,
  setIsViewOnly,
  setIsEditGroup,
}) {
  const dispatch = useDispatch();
  const [currentPage, setCurrentPage] = useState(1);
  // the group awaiting delete confirmation; null when the dialog is closed
  const [pendingDelete, setPendingDelete] = useState(null);

  const isGrid = viewMode === "grid";
  const perPage = isGrid ? GRID_ITEMS_PER_PAGE : ITEMS_PER_PAGE;
  const totalPages = Math.ceil((groups || []).length / perPage);
  const startIdx = (currentPage - 1) * perPage;
  const paginatedGroups = (groups || []).slice(startIdx, startIdx + perPage);
  /* While the groups are being fetched the rows/cards give way to the skeletons,
     so nothing real is rendered underneath them. */
  const visibleGroups = loading ? [] : paginatedGroups;

  const isSpecialUser = localStorage.getItem("IsSpecialUser");

  const handleEditClick = (id) => {
    handleGroupSelect(id);
    setNewGropModalVisibility(true);
    setIsViewOnly(false);
    setIsEditGroup(true);
  };
  const handleRowClick = (id, group) => {
    handleGroupSelect(id);
    setNewGropModalVisibility(true);
    setIsViewOnly(true);
    setIsEditGroup(false);

    console.log("Selected group for view:", group);
  };

  useEffect(() => {
    if (totalPages === 0) {
      if (currentPage !== 1) setCurrentPage(1);
      return;
    }
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const handleDelete = (gp) => dispatch(deleteGroupAsync(gp));

  const isDisabled = (gp) => gp.isGlobal && localStorage.getItem("IsSpecialUser") === "false";

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };

  /* ── Pagination ─────────────────────────────────────── */
  const pageNumbers = [];
  if (totalPages <= 7) {
    for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);
  } else {
    pageNumbers.push(1);
    if (currentPage > 3) pageNumbers.push("...");
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);
    for (let i = start; i <= end; i++) pageNumbers.push(i);
    if (currentPage < totalPages - 2) pageNumbers.push("...");
    pageNumbers.push(totalPages);
  }

  const Pagination = () => {
    /* Shown whenever there are groups, not only past the first page. It is the footer of
       the frame - the row that says how many results there are - and a footer that
       appears and disappears with the page count made the list jump. Same rule as the
       Dashboards / Reports pager. (Was: totalPages <= 1 -> null.) */
    if (!groups || groups.length === 0) return null;
    return (
      <div className="grpl-pagination">
        <span className="grpl-pagination-info">
          Showing {startIdx + 1} to {Math.min(startIdx + perPage, groups.length)} of {groups.length} results
        </span>
        <div className="grpl-pagination-btns">
          <button
            className="grpl-page-btn grpl-page-prev"
            onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            disabled={currentPage === 1}
          >
            ‹ Previous
          </button>
          {pageNumbers.map((p, i) =>
            p === "..." ? (
              <span key={`e-${i}`} className="grpl-page-ellipsis">
                …
              </span>
            ) : (
              <button
                key={p}
                className={`grpl-page-btn grpl-page-num ${currentPage === p ? "grpl-page-active" : ""}`}
                onClick={() => setCurrentPage(p)}
              >
                {String(p).padStart(2, "0")}
              </button>
            ),
          )}
          <button
            className="grpl-page-btn grpl-page-next"
            onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
          >
            Next ›
          </button>
        </div>
      </div>
    );
  };

  /* ── Grid card ──────────────────────────────────────── */
  const GridCard = ({ gp }) => {
    const disabled = isDisabled(gp);
    return (
      <div className="grpl-grid-card">
        <div className="grpl-grid-card-header">
          <div className="grpl-grid-icon-col">
            <TypeIcon groupType={gp.groupType} />
          </div>

          <div className="grpl-grid-title-col">
            <div className="grpl-grid-card-title">{gp.title || "Untitled"}</div>
            <TypePill group={gp} />
          </div>
        </div>

        {/* Meta rows */}
        <div className="grpl-grid-card-meta">
          <div className="grpl-grid-meta-row">
            <span className="grpl-grid-meta-label">Sub-Groups</span>
            <span className="grpl-grid-meta-value grpl-subgroup-count">
              {Array.isArray(gp.children) ? gp.children.length : 0}
            </span>
          </div>
          <div className="grpl-grid-meta-row">
            <span className="grpl-grid-meta-label">Modified</span>
            <span className="grpl-grid-meta-value">{formatDate(gp.updatedAt || gp.createdAt)}</span>
          </div>
        </div>

        {/* Actions */}
        {isSpecialUser && (
          <div className="grpl-grid-card-actions">
            {/* Edit */}
            {disabled ? (
              <span className="grpl-grid-edit-disabled-wrap">
                <button className="grpl-grid-action-btn grpl-grid-edit-btn" disabled>
                  <img src={EditIcon} height={14} alt="edit" className="grpl-edit-icon" />
                  Edit
                </button>
              </span>
            ) : (
              <button
                className="grpl-grid-action-btn grpl-grid-edit-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditClick(gp._id);
                }}
              >
                <img src={EditIcon} height={14} alt="edit" className="grpl-edit-icon" />
                Edit
              </button>
            )}

            {/* Delete — disabled case uses wrapper span for cursor */}
            {disabled ? (
              <span className="grpl-grid-delete-disabled-wrap">
                <button className="grpl-grid-action-btn grpl-grid-delete-btn" disabled>
                  <img src={DeleteIcon} height={14} alt="delete" className="grpl-delete-icon" />
                  Delete
                </button>
              </span>
            ) : (
              <button
                className="grpl-grid-action-btn grpl-grid-delete-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  setPendingDelete(gp);
                }}
              >
                <img src={DeleteIcon} height={14} alt="delete" className="grpl-delete-icon" />
                Delete
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  /* ── Loading skeletons ──────────────────────────────────
     The same stacked loading the Dashboards / Reports, Resources and Schedule
     Reports tables use: placeholder rows keep the page's shape while the groups
     are fetched, instead of a spinner that collapses it. */
  const SkeletonRows = () =>
    Array.from({ length: ITEMS_PER_PAGE }).map((_unused, i) => (
      <tr key={`grpl-sk-${i}`} className="grpl-tr grpl-tr-sk">
        <td className="grpl-td">
          <div className="grpl-name-cell">
            <span className="grpl-sk grpl-sk-icon" />
            <div className="grpl-sk-stack">
              <span className="grpl-sk grpl-sk-line" style={{ width: 170 }} />
              <span className="grpl-sk grpl-sk-line" style={{ width: 90 }} />
            </div>
          </div>
        </td>
        <td className="grpl-td">
          <span className="grpl-sk grpl-sk-line grpl-sk-badge" style={{ width: 68 }} />
        </td>
        <td className="grpl-td grpl-td-center">
          <span className="grpl-sk grpl-sk-line grpl-sk-count" />
        </td>
        <td className="grpl-td">
          <span className="grpl-sk grpl-sk-line" style={{ width: 96 }} />
        </td>
        <td className="grpl-td grpl-td-actions">
          <div className="grpl-actions">
            <span className="grpl-sk grpl-sk-dots" />
            <span className="grpl-sk grpl-sk-dots" />
          </div>
        </td>
      </tr>
    ));

  const SkeletonCards = () =>
    Array.from({ length: GRID_ITEMS_PER_PAGE }).map((_unused, i) => (
      <div key={`grpl-skc-${i}`} className="grpl-grid-card">
        <div className="grpl-grid-card-header">
          <div className="grpl-grid-icon-col">
            <span className="grpl-sk grpl-sk-icon" />
          </div>
          <div className="grpl-grid-title-col">
            <span className="grpl-sk grpl-sk-line" style={{ width: "50%" }} />
            <span className="grpl-sk grpl-sk-line grpl-sk-badge" style={{ width: 62 }} />
          </div>
        </div>

        <div className="grpl-grid-card-meta">
          <div className="grpl-grid-meta-row">
            <span className="grpl-sk grpl-sk-line" style={{ width: 74 }} />
            <span className="grpl-sk grpl-sk-line grpl-sk-count" />
          </div>
          <div className="grpl-grid-meta-row">
            <span className="grpl-sk grpl-sk-line" style={{ width: 62 }} />
            <span className="grpl-sk grpl-sk-line" style={{ width: 88 }} />
          </div>
        </div>

        {isSpecialUser && (
          <div className="grpl-grid-card-actions">
            <span className="grpl-sk grpl-sk-btn" />
            <span className="grpl-sk grpl-sk-btn" />
          </div>
        )}
      </div>
    ));

  /* ── Render ─────────────────────────────────────────── */
  return (
    /* --list / --grid: the pager is the foot of the table card in one and a free-floating
       row in the other - see GroupList.css */
    <div className={`grpl-wrap grpl-wrap--${isGrid ? "grid" : "list"}`}>
      {/* GRID VIEW */}
      {isGrid && (
        <>
          {/* data-drag-scroll: the cards pan by hand as well as by wheel -
              see src/utils/dragToScroll.js */}
          <div className="grpl-grid" data-drag-scroll>
            {loading && <SkeletonCards />}
            {visibleGroups.map((gp) => (
              <GridCard key={gp._id} gp={gp} />
            ))}
          </div>
          <Pagination />
        </>
      )}

      {/* LIST VIEW */}
      {!isGrid && (
        <>
          {/* data-drag-scroll: the rows pan by hand, in either direction */}
          <div className="grpl-table-wrap" data-drag-scroll>
            <table className="grpl-table">
              <thead className="grpl-thead">
                <tr>
                  <th className="grpl-th">Group Name</th>
                  <th className="grpl-th">Type</th>
                  <th className="grpl-th grpl-th-center">Sub-Groups</th>
                  <th className="grpl-th">Last Modified</th>
                  <th className="grpl-th">Actions</th>
                </tr>
              </thead>
              <tbody>
                {loading && <SkeletonRows />}
                {visibleGroups.map((gp) => {
                  const disabled = isDisabled(gp);
                  return (
                    <tr
                      key={gp._id}
                      className="grpl-tr"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleRowClick(gp._id, gp);
                      }}
                    >
                      <td className="grpl-td">
                        <div className="grpl-name-cell">
                          <TypeIcon groupType={gp.groupType} />
                          <span className="grpl-name-text">{gp.title || "Untitled"}</span>
                        </div>
                      </td>
                      <td className="grpl-td">
                        <TypePill group={gp} />
                      </td>
                      <td className="grpl-td grpl-td-center">
                        <span className="grpl-subgroup-count">
                          {Array.isArray(gp.children) ? gp.children.length : 0}
                        </span>
                      </td>
                      <td className="grpl-td grpl-td-muted">{formatDate(gp.updatedAt || gp.createdAt)}</td>
                      <td className="grpl-td grpl-td-actions" onClick={(e) => e.stopPropagation()}>
                        {isSpecialUser ? (
                          <div className="grpl-actions">
                            <EditBtn
                              onClick={(e) => {
                                e.stopPropagation(); // ← ADD THIS
                                handleEditClick(gp._id);
                              }}
                              disabled={disabled}
                            />
                            <DeleteBtn onClick={() => setPendingDelete(gp)} disabled={disabled} />
                          </div>
                        ) : (
                          <span className="grpl-td-muted">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination />
        </>
      )}

      {/* One dialog for both views - the row and the card only say which group */}
      <ConfirmDialog
        open={Boolean(pendingDelete)}
        danger
        title="Delete this group?"
        message={`"${pendingDelete?.title || "Untitled"}" will be removed permanently. This cannot be undone.`}
        onCancel={() => setPendingDelete(null)}
        onConfirm={() => {
          handleDelete(pendingDelete);
          setPendingDelete(null);
        }}
      />
    </div>
  );
}
